import {
    Component,
    OnInit,
    OnDestroy,
    ChangeDetectorRef,
    ElementRef,
    ViewChild,
    HostListener,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ChannelFlags } from 'ops/common/models/device-flags';

import { Observable, Subscription, lastValueFrom } from 'rxjs';
import { VesselService } from '@service/vessel.service';
import { Mimic } from 'ops/common/models/mimic';
import { ElementSettings, presetColorKeys } from 'ops/common/models/editor-viewer-element';
import { MimicService } from '@service/mimic.service';
import { ElementSettingsService } from '@service/element-settings.service';
import { ResponsiveService } from '@service/responsive.service';
import { MimicActionsService } from 'mimic-viewer';
import { ChannelService } from '@service/channel.service';
import { MimicConfirmationNotificationComponent } from '@components/mimic-confirmation-notification/mimic-confirmation-notification.component';
import { MatDialog } from '@angular/material/dialog';
import { Channel } from 'ops/common/models/channel';
import { DeviceFlagsService } from '@service/device-flags.service';
import { ServerSideEventService } from '@service/serverSideEvent.service';

@Component({
    selector: 'app-ops-mimics',
    templateUrl: './ops-mimics.component.html',
    styleUrls: ['./ops-mimics.component.scss'],
    standalone: false
})
export class OpsMimicsComponent implements OnInit, OnDestroy {
    @ViewChild('mimicContainer') mimicContainer: ElementRef;

    selectedMimic: Mimic;
    maxHeight: string;
    maxWidth: string;
    elementsSettings: ElementSettings[];
    defaultColors = presetColorKeys;
    getUpdatedTagValue: Observable<ChannelFlags[]>;
    vesselId: string;
    mimics: Mimic[];

    private subscriptionLoad: Subscription;
    private actionSubscription: Subscription;
    private valueSubscription: Subscription;

    constructor(
        private route: ActivatedRoute,
        private vesselService: VesselService,
        private mimicService: MimicService,
        private elementSettingsService: ElementSettingsService,
        private cdr: ChangeDetectorRef,
        public responsive: ResponsiveService,
        private mimicActionsService: MimicActionsService,
        private channelService: ChannelService,
        private dialog: MatDialog,
        private flagService: DeviceFlagsService,
        private serverSideEvents: ServerSideEventService,
    ) {}

    @HostListener('window:resize')
    handleWindowResize() {
        this.updateDivisionSize();
    }

    async ngOnInit(): Promise<void> {
        this.actionSubscription = this.mimicActionsService.sendActionToFcu$.subscribe(
            async ({ elementId, channelTag, action }) => {
                const channel = await lastValueFrom(
                    this.channelService.getByFcuChannelId(Number(channelTag)),
                );
                if (channel && await this.confirmAction(channel, action)) {
                    //TODO: Send Value to FCU
                    console.log('sending Action to FCU');
                    await new Promise((res) => setTimeout(res, 5000));
                }
                this.mimicActionsService.actionSubjectResponse(elementId);
            },
        );
        this.valueSubscription = this.mimicActionsService.sendValueToFcu$.subscribe(
            async ({ elementId, channelTag, value }) => {
                const channel = await lastValueFrom(
                    this.channelService.getByFcuChannelId(Number(channelTag)),
                );
                if (channel && await this.confirmAction(channel, undefined, value)) {
                    //TODO: Send Value to FCU
                    console.log('sending value to FCU');
                    await new Promise((res) => setTimeout(res, 5000));
                }
                this.mimicActionsService.valueSubjectResponse(elementId);
            },
        );
        await this.updateMimicList();
        this.updateDivisionSize();
        const { id } = await this.vesselService.getVessel();
        this.vesselId = id;
        this.getUpdatedTagValue = this.serverSideEvents.onChannelDataReceived.asObservable();

        this.route.params.subscribe(async (params) => {
            const mimicId = params['id'];
            await this.switchToViewOrDefault(mimicId);
        });

        this.subscriptionLoad = this.vesselService.onVesselUpdated.subscribe(async () => {
            await this.updateMimicList();
        });
    }
    ngOnDestroy(): void {
        this.actionSubscription?.unsubscribe();
        this.valueSubscription?.unsubscribe();
        this.subscriptionLoad?.unsubscribe();
    }

    private async confirmAction(channel: Channel, action?, newValue?) {
        const channelName = channel.opsData.name;
        const units = channel.opsData.measurementUnit;
        let currentValue = undefined;
        if (newValue) {
            currentValue = this.flagService.getFlags(channel)?.value;
        }
        const dialogRef = this.dialog.open(MimicConfirmationNotificationComponent, {
            data: {
                channelName,
                action,
                newValue,
                currentValue,
                units,
            },
        });
        return await lastValueFrom(dialogRef.afterClosed());
    }

    private updateDivisionSize() {
        if (this.mimicContainer) {
            this.maxHeight = this.mimicContainer.nativeElement.offsetHeight;
            this.maxWidth = this.mimicContainer.nativeElement.offsetWidth;
        }
    }

    private async updateMimicList() {
        try {
            this.mimics = await this.mimicService.getAll();
        } catch (err) {
            console.error(err);
        }
    }

    private async switchToViewOrDefault(mimicId: string): Promise<void> {
        this.selectedMimic = null;
        if (!this.mimics || this.mimics.length === 0) {
            return;
        }

        if (!mimicId) {
            await this.getMimicSettings(this.mimics[0].id);
            this.selectedMimic = this.mimics[0];
        }

        this.mimics.forEach(async (mimic) => {
            if (mimic.id === mimicId) {
                await this.getMimicSettings(mimicId);
                this.selectedMimic = mimic;
                if (this.selectedMimic && !this.selectedMimic.svgcontent) {
                    this.selectedMimic.svgcontent = this.getEmptySvg(this.selectedMimic);
                }
                this.cdr.detectChanges();
            }
        });

        this.cdr.detectChanges();
    }

    private async getMimicSettings(mimicId) {
        try {
            this.elementsSettings = await this.elementSettingsService.getAllByMimicId(mimicId);
        } catch (err) {
            console.error(err);
        }
    }

    private getEmptySvg(mimic: Mimic) {
        return (
            '<svg id="' +
            mimic.name +
            '" width="' +
            (mimic.profile.width - 50) * 0.8 +
            '" height="' +
            (mimic.profile.height - 65) * 0.8 +
            '" xmlns="http://www.w3.org/2000/svg" xmlns:svg="http://www.w3.org/2000/svg">' +
            '<filter id="blur-filter" x="-3" y="-3" width="200" height="200"><feGaussianBlur in="SourceGraphic" stdDeviation="3" /></filter>' +
            '<g class="layer"><title>Layer 1</title></g></svg>'
        );
    }
}
